import { useMemo, useState } from "react";
import {
  Box,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  makeStyles,
} from "@material-ui/core";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import GetAppIcon from "@material-ui/icons/GetApp";
import DataTable, { TableColumn } from "react-data-table-component";

import { tableCustomStyles } from "../../../utils/table-styles";
import PaginatedTableTop, {
  paginationTopClassName,
} from "../../shared/paginated-table-top";
import { RoyaltyReportLine } from "../../../interface/api/RoyaltyReportLine";
import { SkippedTicket } from "../../../interface/api/SkippedTicket";
import { RoyaltyLineFilter } from "../../../interface/RoyaltyLineFilter";
import { formatDate } from "../../../utils/date-helper";
import { formatCurrency } from "../../../utils/parser";
import {
  exportRoyaltyLines,
  exportSkippedTickets,
} from "../../../utils/royalty-export";
import {
  SkippedTicketReason,
  StatusColors,
  royaltyStreamLabel,
} from "../../../utils/constants";

// Yellow still needs a price back-filled; blue settled on the period average. A reader
// scrolling the lines can tell at a glance which rows the total was inferred from.
const lineRowStyles = [
  {
    when: (row: RoyaltyReportLine) => row.estimatedSalePrice,
    style: { backgroundColor: StatusColors.infoBg },
  },
  {
    when: (row: RoyaltyReportLine) =>
      row.missingSalePrice && !row.estimatedSalePrice,
    style: { backgroundColor: StatusColors.warningBg },
  },
];

const skippedRowStyles = [
  { when: () => true, style: { backgroundColor: StatusColors.warningBg } },
];

const matches = (line: RoyaltyReportLine, filter: RoyaltyLineFilter) => {
  if (filter === "priced") return !line.missingSalePrice;
  if (filter === "estimated") return line.estimatedSalePrice;
  if (filter === "unpriced") {
    return line.missingSalePrice && !line.estimatedSalePrice;
  }
  return true;
};

const filterLabels: Record<RoyaltyLineFilter, string> = {
  all: "All",
  priced: "Priced",
  estimated: "Estimated",
  unpriced: "Unpriced",
  skipped: "Skipped",
};

const lineFilters: RoyaltyLineFilter[] = [
  "all",
  "priced",
  "estimated",
  "unpriced",
];

const priceCell = (row: RoyaltyReportLine) => {
  // A net-priced line settles on the net ticket price; the sale price is for reference.
  if (row.netTicketPrice != null) {
    return row.salePrice != null ? formatCurrency(row.salePrice) : "—";
  }
  if (!row.missingSalePrice) return formatCurrency(row.salePrice);
  if (row.estimatedSalePrice) {
    return `Estimated — ${formatCurrency(row.effectiveSalePrice)}`;
  }
  return "Unpriced — $0.00";
};

const streamColumn = <T extends { stream: string }>(): TableColumn<T> => ({
  name: "Stream",
  selector: (row) => royaltyStreamLabel(row.stream),
  sortable: true,
});

// Stream only shows on an All Streams run, Net Ticket Price only when a line used one,
// so a single Main Pit or Rubble run reads as it always has.
const buildLineColumns = (
  grouped: boolean,
  showNetPrice: boolean,
): TableColumn<RoyaltyReportLine>[] => [
  ...(grouped ? [streamColumn<RoyaltyReportLine>()] : []),
  {
    name: "Date",
    selector: (row) => (row.date ? formatDate(row.date, false) : ""),
    sortable: true,
  },
  {
    name: "Ticket #",
    selector: (row) => `${row.prefix}-${row.ticketNumber}`,
    sortable: true,
  },
  {
    name: "Customer",
    selector: (row) => row.customer ?? "",
    sortable: true,
  },
  {
    name: "Source / Job Site",
    selector: (row) => row.jobSite ?? "",
    sortable: true,
    wrap: true,
  },
  {
    name: "Selling Price",
    selector: (row) => priceCell(row),
    sortable: true,
  },
  ...(showNetPrice
    ? [
        {
          name: "Net Ticket Price",
          selector: (row: RoyaltyReportLine) =>
            row.netTicketPrice != null ? formatCurrency(row.netTicketPrice) : "",
        },
      ]
    : []),
  {
    name: "Adjusted Ticket Price",
    selector: (row) => formatCurrency(row.adjustedTicketPrice),
  },
  {
    name: "Difference",
    selector: (row) => formatCurrency(row.difference),
    sortable: true,
  },
  {
    name: "Royalty Owed",
    selector: (row) => formatCurrency(row.royalty),
    sortable: true,
  },
];

const baseSkippedColumns: TableColumn<SkippedTicket>[] = [
  {
    name: "Date",
    selector: (row) => (row.date ? formatDate(row.date, false) : ""),
    sortable: true,
  },
  {
    name: "Ticket #",
    selector: (row) => `${row.prefix}-${row.ticketNumber}`,
    sortable: true,
  },
  {
    name: "Why it was skipped",
    selector: () => SkippedTicketReason,
  },
];

const tableProps = {
  customStyles: tableCustomStyles,
  className: paginationTopClassName,
  striped: true,
  pagination: true,
  paginationPerPage: 50,
  paginationRowsPerPageOptions: [50, 100, 150, 200],
};

const useStyles = makeStyles((theme) => ({
  bar: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  legend: {
    color: theme.palette.text.secondary,
    display: "flex",
    cursor: "help",
  },
  export: {
    marginLeft: "auto",
  },
}));

/**
 * The tickets behind the total, filterable down to the ones that still need a price —
 * finding those in a 10,000-row workbook is what the back-fill kept stalling on — and
 * across to the blanks that never reached the report at all. Skipped tickets are a
 * different population rather than a slice of the lines, so they bring their own
 * columns. Every view exports whatever it is showing.
 */
const RoyaltyReportLines = ({
  lines,
  grouped = false,
  skipped,
  unlistedUnpricedCount = 0,
  fileName = "Royalty Report Lines",
}: {
  lines: RoyaltyReportLine[];
  /** An All Streams run: lines arrive grouped by stream and carry a Stream column. */
  grouped?: boolean;
  /** Tickets the run refused to bill for, shown under their own filter. */
  skipped: SkippedTicket[];
  /** Unpriced tickets the run left out entirely, so the filter cannot show them. */
  unlistedUnpricedCount?: number;
  /** Names the exported workbook; the active filter is appended to it. */
  fileName?: string;
}) => {
  const classes = useStyles();
  const [filter, setFilter] = useState<RoyaltyLineFilter>("all");

  const counts = useMemo(() => {
    const byFilter = lineFilters.reduce(
      (totals, key) => ({
        ...totals,
        [key]: lines.filter((line) => matches(line, key)).length,
      }),
      {} as Record<RoyaltyLineFilter, number>,
    );
    byFilter.skipped = skipped.length;
    return byFilter;
  }, [lines, skipped]);

  const shown = useMemo(
    () => lines.filter((line) => matches(line, filter)),
    [lines, filter],
  );

  const showingSkipped = filter === "skipped";
  const showNetPrice = lines.some((line) => line.netTicketPrice != null);

  const lineColumns = useMemo(
    () => buildLineColumns(grouped, showNetPrice),
    [grouped, showNetPrice],
  );
  const skippedColumns = useMemo(
    () =>
      grouped
        ? [streamColumn<SkippedTicket>(), ...baseSkippedColumns]
        : baseSkippedColumns,
    [grouped],
  );

  const filters: RoyaltyLineFilter[] =
    skipped.length > 0 ? [...lineFilters, "skipped"] : lineFilters;

  const onExport = () => {
    const name = `${fileName}${
      filter === "all" ? "" : ` - ${filterLabels[filter]}`
    }`;
    return showingSkipped
      ? exportSkippedTickets(skipped, name, grouped)
      : exportRoyaltyLines(shown, name, grouped, showNetPrice);
  };

  return (
    <>
      <Box className={classes.bar}>
        {filters.map((key) => (
          <Chip
            key={key}
            size="small"
            label={`${filterLabels[key]} (${counts[key].toLocaleString()})`}
            color={filter === key ? "primary" : "default"}
            variant={filter === key ? "default" : "outlined"}
            onClick={() => setFilter(key)}
          />
        ))}
        <Tooltip
          title="Blue rows were valued at the period average; yellow rows have no selling price and are in no total. Hydrovac and Small Quantities settle on the net ticket price. Skipped tickets never reached the report at all."
          enterTouchDelay={0}
        >
          <span className={classes.legend}>
            <HelpOutlineIcon fontSize="small" />
          </span>
        </Tooltip>

        <Tooltip title="Export these rows to Excel" enterTouchDelay={0}>
          <span className={classes.export}>
            <IconButton
              aria-label="Export to Excel"
              size="small"
              color="primary"
              disabled={(showingSkipped ? skipped.length : shown.length) === 0}
              onClick={onExport}
            >
              <GetAppIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {unlistedUnpricedCount > 0 && !showingSkipped && (
        <Typography
          variant="caption"
          color="textSecondary"
          component="p"
          style={{ marginBottom: 8, color: StatusColors.warning }}
        >
          {unlistedUnpricedCount.toLocaleString()} unpriced tickets are not listed
          here. Re-run with &quot;Include tickets with no selling price&quot; to see
          which ones.
        </Typography>
      )}

      <PaginatedTableTop>
        {showingSkipped ? (
          <DataTable
            {...tableProps}
            columns={skippedColumns}
            data={skipped}
            conditionalRowStyles={skippedRowStyles}
            noDataComponent="No tickets were skipped in this run."
          />
        ) : (
          <DataTable
            {...tableProps}
            columns={lineColumns}
            data={shown}
            conditionalRowStyles={lineRowStyles}
            noDataComponent={
              filter === "all"
                ? "No tickets settled in the selected range."
                : `No ${filterLabels[filter].toLowerCase()} tickets in this run.`
            }
          />
        )}
      </PaginatedTableTop>
    </>
  );
};

export default RoyaltyReportLines;
